import useAdminToast from './useAdminToast';
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Text, List, ListItem, ListIcon, Alert,
} from '@chakra-ui/react';
import { AdminAlertIcon, CloseIcon, WarningIcon } from './AdminIcon';
import AdminState from './AdminState';
import { useAnimationToggle } from '../../Contexts/AnimationContext';

/** Archive confirmation keeps its context, keyboard focus and retry on failure. */
export default function ArchiveResourceDialog({ label, name, loadAffected, onArchive, onArchived, onClose, finalFocusRef, warning, affectedDescription, emptyDescription }) {
  const toast = useAdminToast();
  const { animationsEnabled } = useAnimationToggle();
  const cancelRef = useRef(null);
  const submittingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [affected, setAffected] = useState([]);
  const [retry, setRetry] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError(false);
    async function load() {
      try {
        const response = await loadAffected();
        if (response.error || response.status !== 200 || !Array.isArray(response.items)) throw new Error('load_failed');
        if (current) setAffected(response.items);
      } catch {
        if (current) setLoadError(true);
      } finally {
        if (current) setLoading(false);
      }
    }
    load();
    return () => { current = false; };
  }, [loadAffected, retry]);

  const close = () => { if (!submittingRef.current) onClose(); };
  const archive = async () => {
    if (loading || loadError || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSaveError(false);
    try {
      const response = await onArchive();
      if (response && (response.error || response.status === 0 || response.status >= 400)) throw new Error('archive_failed');
      toast({ title: `${label.charAt(0).toUpperCase() + label.slice(1)} archived` });
      onArchived();
    } catch {
      setSaveError(true);
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={close} isCentered size="lg" scrollBehavior="inside"
      initialFocusRef={cancelRef} finalFocusRef={finalFocusRef}
      closeOnOverlayClick={!submitting} closeOnEsc={!submitting}
      motionPreset={animationsEnabled ? 'scale' : 'none'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Archive {label}</ModalHeader>
        <ModalCloseButton isDisabled={submitting} aria-label="Close archive dialog"><CloseIcon /></ModalCloseButton>
        <ModalBody>
          <Text mb={4}>Are you sure you want to archive <strong>{name}</strong>?</Text>
          {warning && <Alert status="warning" mb={4}><AdminAlertIcon status="warning" />{warning}</Alert>}
          {loading ? <AdminState status="loading" title="Loading affected tool instances…" /> :
            loadError ? <AdminState status="error" title="Affected tool instances could not be loaded"
              description="Review the affected instances before archiving. Try loading them again."
              onRetry={() => setRetry(value => value + 1)} /> :
              affected.length ? <>
                <Text mb={2} fontWeight="medium">{affectedDescription} ({affected.length}):</Text>
                <List spacing={2}>
                  {affected.map(instance => <ListItem key={instance.id}>
                    <ListIcon as={WarningIcon} color="orange.500" />{instance.name}
                  </ListItem>)}
                </List>
              </> : <Text color="gray.600">{emptyDescription}</Text>}
          {saveError && <AdminState status="error" mt={4} mb={0} title={`Could not archive this ${label}`}
            description="The request could not be completed. Try again." />}
        </ModalBody>
        <ModalFooter>
          <Button ref={cancelRef} variant="outline" onClick={close} isDisabled={submitting}>Cancel</Button>
          <Button variant="solid" colorScheme="red" onClick={archive} isDisabled={loading || loadError}
            isLoading={submitting} aria-label={submitting ? 'Archiving…' : undefined}>Archive</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
